import React, { useState, useEffect, FunctionComponent } from "react"
import { useNavigate } from "react-router-dom"

import { toast } from "react-toastify"
import dayjs from "dayjs"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import Button from "@murphy-frontend/web-core/components/Button"
import SearchBar from "@murphy-frontend/web-core/components/SearchBar"

import { rolesUser, courseLevels } from "@murphy-frontend/web-core/constants"
import { useGetUsers } from "@murphy-frontend/common/api/Users/Queries"
import { useDeleteUserMutation } from "@murphy-frontend/common/api/Users/Mutations"
import { CustomerUserTrainingSessionStates, Roles, TrainingTypes } from "@murphy-frontend/common/enums"
import { useGetTrainingWithCount } from "../../common/api/Training/queries"
import { getConvertedDate } from "@murphy-frontend/common/services/TimeService"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { useUserPreferences } from "../../common/contexts/UserPreferencesContext"
import { useCustomer } from "../../common/contexts/CustomerContext"
import { useUser } from "../../common/contexts/UserContext"
import { UserListTrainingSessionViewModel } from "@murphy-frontend/web-core/features/UsersAdmin/components/UsersList"
import ComponentWrapper from "../../common/components/ComponentWrapper"
import Table from "@murphy-frontend/web-core/components/Table"
import { IconContext } from "react-icons/lib"
import { FaCheck } from "react-icons/fa6"
import { ColumnHeaderCell, ColumnRowCell } from "@murphy-frontend/web-core/components/Table/Models"
import { CourseColumns, UserColumns, EducationColumns } from "@murphy-frontend/web-core/columns"
import FilterTag from "@murphy-frontend/web-core/components/FilterTag"
import TabPanel, { TabType } from "@murphy-frontend/web-core/components/Tabs/TabPanel"
import { FilterType } from "@murphy-frontend/web-core/components/FilterControl"
import UserFilterModal from "./components/UserFilterModal"
import AddUserModal from "./components/AddUserModal"
import EditUserModal from "./components/EditUserModal"
import ActionsRow from "./components/ActionsRow"
import EducationByCourseActions from "./components/EducationByCourseActions"
import DeleteConfirmationBody from "./components/DeleteConfirmationBody"
import { convertLevels, convertRoles } from "@murphy-frontend/common/utils"
import FiltersRow from "./components/FiltersRow"

const UsersAdmin: FunctionComponent = () => {
	const navigate = useNavigate()
	const { openModal, closeModal } = useModal()
	const {
		translations,
		timeZone,
		selectedRole,
		selectedUserCourseLevels,
		educationSelectedRole,
		isMobileApp,
		setEducationSelectedRole,
		setSelectedUserCourseLevels,
		setSelectedRole,
		setIsMobileApp
	} = useUserPreferences()

	const [error, setError] = useState(null)
	const [userRows, setUserRows] = useState<ColumnRowCell[][]>([])
	const [userColumns, setUserColumns] = useState<ColumnHeaderCell[]>([])
	const [userTableData, setUserTableData] = useState<ColumnRowCell[][]>([])
	const [educationByUserRows, setEducationByUserRows] = useState<ColumnRowCell[][]>([])
	const [educationByUserColumns, setEducationByUserColumns] = useState<ColumnHeaderCell[]>([])
	const [educationByUserTableData, setEducationByUserTableData] = useState<ColumnRowCell[][]>([])
	const [educationByCourseRows, setEducationByCourseRows] = useState<ColumnRowCell[][]>([])
	const [educationByCourseColumns, setEducationByCourseColumns] = useState<ColumnHeaderCell[]>([])
	const [educationByCourseTableData, setEducationByCourseTableData] = useState<ColumnRowCell[][]>([])
	const [selectedTabId, setSelectedTabId] = useState<number>(0)
	const [userFilters, setUserFilters] = useState<FilterType[]>([])
	const [educationByUserFilters, setEducationByUserFilters] = useState<FilterType[]>([])
	const [educationByCourseFilters, setEducationByCourseFilters] = useState<FilterType[]>([])

	const [currentSearchText, setCurrentSearchText] = useState("")
	const [isUserView, setIsUserView] = useState(true)

	const { customer, allCustomerProducts } = useCustomer()
	const { user } = useUser()
	const { mutate: mutateDel, isPending: isDeleteLoading } = useDeleteUserMutation(translations)
	

	if (user.Role !== Roles.Admin) {
		navigate("/portal")
		toast("Unauthorized")
		return null
	}

	const {
		isLoading: isLoadingCoursesCount,
		isError: isErrorCoursesCount,
		data: dataCoursesCount,
		error: errorCoursesCount
	} = useGetTrainingWithCount({
		customerId: customer.Id
	})

	const {
		isLoading: isLoadingUsers,
		isError: isErrorUsers,
		data: dataUsers,
		error: errorUsers,
		isPending: isPendingUsers
	} = useGetUsers(customer.Id)

	const onClickTab = (tabId: number) => {
		setSelectedTabId(tabId)
	}

	const generateInitialUserFilters = () => {
		const mappedRoles = rolesUser.map(p => {
			return {
				displayName: convertRoles(p.id, translations),
				columnname: "role",
				filtervalue: p.id,
				isActive: selectedRole.includes(p.id)
			}
		})

		const mobileCheckbox = [
			{
				displayName: translations.mobileApp,
				columnname: "mobile",
				filtervalue: 0,
				isActive: isMobileApp
			}
		]

		const allPossibleFilters = [...mappedRoles, ...mobileCheckbox]

		return allPossibleFilters
	}

	const generateInitialEducationByUserFilters = () => {
		const mappedRoles = rolesUser.map(p => {
			return {
				displayName: convertRoles(p.id, translations),
				columnname: "role",
				filtervalue: p.id,
				isActive: educationSelectedRole.includes(p.id)
			}
		})

		const allPossibleFilters = [...mappedRoles]

		return allPossibleFilters
	}

	const generateInitialEducationByCourseFilters = () => {
		const mappedLevels = courseLevels.map(p => {
			return {
				displayName: convertLevels(p.id, translations),
				columnname: "level",
				filtervalue: p.id,
				isActive: selectedUserCourseLevels.includes(p.id)
			}
		})

		const allPossibleFilters = [...mappedLevels]

		return allPossibleFilters
	}

	const initialTabs: TabType[] = [
		{
			id: 0,
			displayText: translations.userManagement,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		},
		{
			id: 1,
			displayText: translations.educationManagement,
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-default-panel-tab-selected wrap-text"
		}
	]

	useEffect(() => {
		if (translations) {
			const initialUserFilters = generateInitialUserFilters()
			setUserFilters(initialUserFilters)
			const initialEducationByUserFilters = generateInitialEducationByUserFilters()
			setEducationByUserFilters(initialEducationByUserFilters)
			const initialEducationByCourseFilters = generateInitialEducationByCourseFilters()
			setEducationByCourseFilters(initialEducationByCourseFilters)
		}
	}, [translations, selectedRole, isMobileApp, educationSelectedRole, selectedUserCourseLevels])

	const onClickDeleteUser = (userId: string) => {
		const userData = dataUsers.find(user => user.UserId === userId)
		const modalConf: ModalConfiguration = {
			body: (
				<DeleteConfirmationBody
					userName={userData.Username}
					translations={translations}
					customerUserId={userData.CustomerUserId}
					closeModal={closeModal}
				/>
			),
			hideButton: true,
			hideCloseIcon: true,
			position: "center"
		}

		openModal(modalConf)
	}

	const onSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value
		setCurrentSearchText(newValue)
	}

	const roleTag = (role: number) => {
		const field = rolesUser.find(_ => _.id === role)

		return <FilterTag filterName={translations[field.translationKey]} role={role} />
	}

	const levelTag = (level: number) => {
		const field = convertLevels(level, translations)

		return <FilterTag filterName={field} level={level} />
	}

	useEffect(() => {
		if (translations) {
			setUserColumns(UserColumns(translations))
			setEducationByUserColumns(EducationColumns(translations))
			setEducationByCourseColumns(CourseColumns(translations))
		}
	}, [translations])

	useEffect(() => {
		let roleRowsEdu = educationByUserFilters.filter(filter => filter.columnname === "role" && filter.isActive)
		let eduRowCpy = [...educationByUserRows]

		if (roleRowsEdu.length) {
			const ids = [...roleRowsEdu].map((role: FilterType) => role.displayName)
			eduRowCpy = eduRowCpy.filter(rows => ids.includes(rows[1].value))
		}
		if (currentSearchText) {
			eduRowCpy = eduRowCpy.filter(rows => {
				const searchValue = currentSearchText.toUpperCase()
				const email = rows[0].value || ""
				const username = rows[1].value || ""

				return (
					email.toString().toUpperCase().startsWith(searchValue) ||
					username.toString().toUpperCase().startsWith(searchValue)
				)
			})
		}

		setEducationByUserTableData(eduRowCpy)
	}, [educationByUserFilters, currentSearchText, educationByUserRows])

	useEffect(() => {
		let rowsLevel = educationByCourseFilters.filter(filter => filter.columnname === "level" && filter.isActive)
		let rowCpy = [...educationByCourseRows]

		if (rowsLevel.length) {
			const ids = [...rowsLevel].map((course: FilterType) => course.displayName)
			rowCpy = rowCpy.filter(rows => ids.includes(rows[1].value))
		}

		if (currentSearchText) {
			rowCpy = rowCpy.filter(rows => {
				const searchValue = currentSearchText.toUpperCase()
				const username = rows[0].value || ""

				return username.toString().toUpperCase().startsWith(searchValue)
			})
		}

		setEducationByCourseTableData(rowCpy)
	}, [educationByCourseFilters, currentSearchText, educationByCourseRows])

	useEffect(() => {
		let roleRows = userFilters.filter(filter => filter.columnname === "role" && filter.isActive)
		let mobileRows = userFilters.filter(filter => filter.columnname === "mobile" && filter.isActive)
		let rowCpy = [...userRows]

		if (roleRows.length && mobileRows.length) {
			const ids = [...roleRows].map((role: FilterType) => role.filtervalue)
			rowCpy = rowCpy.filter(rows => ids.includes(rows[3].value) || rows[4].value)
		} else if (roleRows.length) {
			const ids = [...roleRows].map((role: FilterType) => role.filtervalue)
			rowCpy = rowCpy.filter(rows => ids.includes(rows[3].value))
		} else if (mobileRows.length) {
			rowCpy = rowCpy.filter(rows => rows[4].value)
		}

		if (currentSearchText) {
			rowCpy = rowCpy.filter(rows => {
				const searchValue = currentSearchText.toUpperCase()
				const email = rows[0].value || ""
				const username = rows[1].value || ""

				return (email.toString().toUpperCase().startsWith(searchValue) || username.toString().toUpperCase().startsWith(searchValue))
			})
		}

		setUserTableData(rowCpy)
	}, [userFilters, currentSearchText, userRows])

	const onClickEditUser = (userId: string) => {
		const userData = dataUsers.find(user => user.UserId === userId)
		const modalConf: ModalConfiguration = {
			body: (
				<EditUserModal
					closeModal={closeModal}
					translations={translations}
					products={allCustomerProducts}
					userData={userData}
				/>
			),
			position: "center",
			buttonText: "Ok",
			hideButton: true
		}

		openModal(modalConf)
	}

	const goToUser = (id: string) => {
		if (id === user.CustomerUserId) {
			navigate("/me")
		}
		else {
			navigate(`/users/${id}`)
		}
	}

	useEffect(() => {
		if (!isLoadingUsers && timeZone) {
			let dataUsersCpyEdu = [...dataUsers]

			let newRowsEducation = dataUsersCpyEdu.map((user, idx) => {
				const mappedUserTrainingSessions = user?.UserTrainingSessions?.map(
					uts =>
						({
							id: uts.TrainingId,
							date: getConvertedDate(dayjs.utc(uts.Date), timeZone),
							trainingTypeId: uts.TrainingTypeId,
							trainingSessionStateId: uts.TrainingSessionStateID
						}) as UserListTrainingSessionViewModel
				)
				const completedCourses =
					mappedUserTrainingSessions?.filter(
						row =>
							row.trainingTypeId === TrainingTypes.Course &&
							row.trainingSessionStateId === CustomerUserTrainingSessionStates.Completed
					) ?? []

				const bookedCourses =
					mappedUserTrainingSessions?.filter(
						row =>
							row.trainingTypeId === TrainingTypes.Course &&
							row.trainingSessionStateId === CustomerUserTrainingSessionStates.Booked
					) ?? []

				return [
					{
						id: user.UserId,
						rowId: idx,
						displayValue: (
							<span className='table-column-link' onClick={() => goToUser(user.CustomerUserId)}>
								{user.Username}
							</span>
						),
						value: user.Username,
						columnKey: "userName"
					},
					{
						id: user.UserId,
						rowId: idx,
						displayValue: roleTag(user.Role),
						// translated for sorting
						value: convertRoles(user.Role, translations),
						customClass: "text-center",
						columnKey: "role"
					},
					{
						id: user.UserId,
						rowId: idx,
						displayValue: completedCourses.length,
						value: completedCourses.length,
						columnKey: "coursesCompleted"
					},
					{
						id: user.UserId,
						rowId: idx,
						displayValue: bookedCourses.length,
						value: bookedCourses.length,
						columnKey: "bookedCourses"
					}
				]
			})

			setEducationByUserRows(newRowsEducation)

			let dataUsersCpy = [...dataUsers]

			let newRows = dataUsersCpy.map((user, idx) => {
				return [
					{
						id: user.UserId,
						rowId: idx,
						displayValue: (
							<span className='table-column-link' onClick={() => goToUser(user.CustomerUserId)}>
								{user.Username}
							</span>
						),
						value: user.Username,
						columnKey: "userName"
					},
					{
						id: user.UserId,
						rowId: idx,
						displayValue: user.Email,
						value: user.Email,
						columnKey: "email"
					},
					{
						id: user.UserId,
						rowId: idx,
						displayValue: user.Phonenumber,
						value: user.Phonenumber,
						columnKey: "phoneNumber"
					},
					{
						id: user.UserId,
						rowId: idx,
						displayValue: roleTag(user.Role),
						value: user.Role,
						customClass: "text-center",
						columnKey: "role"
					},
					{
						id: user.UserId,
						rowId: idx,
						displayValue: user.DeviceToken ? (
							<IconContext.Provider value={{ className: "btn-icon" }}>
								<FaCheck />
							</IconContext.Provider>
						) : (
							""
						),
						value: user.DeviceToken,
						customClass: "text-center",
						columnKey: "mobileApp"
					},
					{
						id: user.UserId,
						rowId: idx,
						displayValue: (
							<ActionsRow
								userId={user.UserId}
								onEditUser={onClickEditUser}
								onDeleteUser={onClickDeleteUser}
								userName={user.Username}
								customerUserId={user.CustomerUserId}
								translations={translations}
							/>
						),
						value: user.DeviceToken,
						customClass: "text-center",
						columnKey: "actionRow"
					}
				]
			})

			setUserRows(newRows)
		}
	}, [dataUsers, isLoadingUsers, timeZone, isPendingUsers])

	useEffect(() => {
		if (!isLoadingCoursesCount) {
			const dataCoursesCountRows = dataCoursesCount.map((course, idx) => {
				return [
					{
						id: course.Id,
						rowId: idx,
						displayValue: course.Name,
						value: course.Name,
						columnKey: "name"
					},
					{
						id: course.Id,
						rowId: idx,
						displayValue: course.Levels ? levelTag(course.Levels) : "",
						// translated for sorting
						value: course.Levels ? convertLevels(course.Levels, translations) : "Z",
						customClass: "text-center",
						columnKey: "level"
					},
					{
						id: course.Id,
						rowId: idx,
						displayValue: course.UsersCompleted,
						value: course.UsersCompleted,
						columnKey: "usersCompleted"
					},
					{
						id: course.Id,
						rowId: idx,
						displayValue: course.UsersBooked,
						value: course.UsersBooked,
						columnKey: "usersBooked"
					},
					{
						id: course.Id,
						rowId: idx,
						displayValue: (
							<>
							  {course.MandatoryForUsers && roleTag(0)}
							  {course.MandatoryForAdmins && roleTag(1)}
							</>
						  ),
						value: course.Id,
						columnKey: "mandatoryStatus"
					},
					{
						id: course.Id,
						rowId: idx,
						displayValue: (
							<EducationByCourseActions
								trainingId={course.Id}
								customerId={customer.Id}
								translations={translations}
							/>
						),
						value: course.Id,
						customClass: "text-center",
						columnKey: "actionRow"
					}
				]
			})

			setEducationByCourseRows(dataCoursesCountRows)
		}
	}, [isLoadingCoursesCount, dataCoursesCount])
	const ErrorMessage = () => <p>{error}</p>

	useEffect(() => {
		setError(errorUsers)
	}, [isErrorUsers])

	useEffect(() => {
		setError(errorCoursesCount)
	}, [isErrorCoursesCount])

	useEffect(() => {
		setCurrentSearchText("")
	}, [selectedTabId])

	useEffect(() => {
		if (error) {
			const modalConf: ModalConfiguration = {
				title: "Error",
				body: <ErrorMessage />,
				buttonText: "Ok",
				type: "error",
				okCallback: () => {
					setError(null)
				}
			}
			openModal(modalConf)
		}
	}, [error])

	const filterToggleHelper = (filters: FilterType[], filterRow: FilterType) => {
		return [...filters].map(filter => {
			const updatedItem = { ...filter }

			if (filter.columnname === filterRow.columnname && filter.filtervalue === filterRow.filtervalue) {
				updatedItem.isActive = !filter.isActive
			}

			return updatedItem
		})
	}

	const onToggleFilter = (filt: FilterType, filterName: string) => {
		let updatedFilter: FilterType[] = []

		switch (filterName) {
			case "user":
				updatedFilter = filterToggleHelper(userFilters, filt)
				setUserFilters(updatedFilter)
				syncSelectedRole(updatedFilter)
				break
			case "education":
				updatedFilter = filterToggleHelper(educationByUserFilters, filt)
				setEducationByUserFilters(updatedFilter)
				syncSelectedRoleEdu(updatedFilter)
				break
			case "course":
				updatedFilter = filterToggleHelper(educationByCourseFilters, filt)
				setEducationByCourseFilters(updatedFilter)
				syncSelectedCourseUser(updatedFilter)
				break
		}
	}

	const resetUserFilters = () => {
		const allFilters = [...userFilters].map(filter => {
			return { ...filter, isActive: false }
		})

		setUserFilters(allFilters)
		setCurrentSearchText("")
		syncSelectedRole(allFilters)
	}

	const resetEducationByUserFilters = () => {
		const allFilters = [...educationByUserFilters].map(filter => {
			return { ...filter, isActive: false }
		})

		setEducationByUserFilters(allFilters)
		setCurrentSearchText("")
		syncSelectedRoleEdu(allFilters)
	}

	const resetEducationByCourseFilters = () => {
		const allFilters = [...educationByCourseFilters].map(filter => {
			return { ...filter, isActive: false }
		})

		setEducationByCourseFilters(allFilters)
		setCurrentSearchText("")
		syncSelectedCourseUser(allFilters)
	}

	const syncSelectedRoleEdu = (filters: FilterType[]) => {
		const roleFilters = filters.filter(p => p.columnname === "role" && p.isActive === true).map(p => p.filtervalue)
		setEducationSelectedRole(roleFilters)
	}

	const syncSelectedRole = (filters: FilterType[]) => {
		const roleFilters = filters.filter(p => p.columnname === "role" && p.isActive === true).map(p => p.filtervalue)
		setSelectedRole(roleFilters)
		const mobileFilters = filters.find(p => p.columnname === "mobile")
		setIsMobileApp(mobileFilters.isActive)
	}

	const syncSelectedCourseUser = (filters: FilterType[]) => {
		const courseFilters = filters
			.filter(p => p.columnname === "level" && p.isActive === true)
			.map(p => p.filtervalue)
		setSelectedUserCourseLevels(courseFilters)
	}

	const onUpdateUserFilters = (updatedFilters: FilterType[]) => {
		setUserFilters(updatedFilters)
		syncSelectedRole(updatedFilters)
	}

	const onUpdateEducationByCourseFilters = (updatedFilters: FilterType[]) => {
		setEducationByCourseFilters(updatedFilters)
		syncSelectedCourseUser(updatedFilters)
	}

	const onUpdateEducationByUserFilters = (updatedFilters: FilterType[]) => {
		setEducationByUserFilters(updatedFilters)
		syncSelectedRoleEdu(updatedFilters)
	}

	const onClickFilter = () => {
		const userFiltersModalConfiguration: ModalConfiguration = {
			body: (
				<UserFilterModal
					allFilters={userFilters}
					closeModal={closeModal}
					toggleFilter={onUpdateUserFilters}
					reset={resetUserFilters}
					translations={translations}
				/>
			),
			buttonText: "Ok",
			hideButton: true
		}

		const educationByUserFiltersModalConfiguration: ModalConfiguration = {
			body: (
				<UserFilterModal
					allFilters={educationByUserFilters}
					closeModal={closeModal}
					toggleFilter={onUpdateEducationByUserFilters}
					reset={resetEducationByUserFilters}
					translations={translations}
				/>
			),
			buttonText: "Ok",
			hideButton: true
		}

		const educationByCourseFiltersModalConfiguration: ModalConfiguration = {
			body: (
				<UserFilterModal
					allFilters={educationByCourseFilters}
					closeModal={closeModal}
					toggleFilter={onUpdateEducationByCourseFilters}
					reset={resetEducationByCourseFilters}
					translations={translations}
				/>
			),
			buttonText: "Ok",
			hideButton: true
		}

		if (selectedTabId === 0) {
			openModal(userFiltersModalConfiguration)
		}
		else {
			if (isUserView) {
				openModal(educationByUserFiltersModalConfiguration)
			}
			else {
				openModal(educationByCourseFiltersModalConfiguration)
			}
		}
	}

	if (isLoadingUsers || isDeleteLoading || isLoadingCoursesCount) {
		return (
			<ComponentWrapper active={8} subMenuActive={2}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	const onClickAddUser = () => {
		const modalConf: ModalConfiguration = {
			body: (
				<AddUserModal
					closeModal={closeModal}
					translations={translations}
					products={allCustomerProducts}
					customerId={customer.Id}
				/>
			),
			position: "center",
			buttonText: "Ok",
			hideButton: true
		}

		openModal(modalConf)
	}

	return (
		<ComponentWrapper active={8} subMenuActive={2}>
			<section className='header-btns'>
				<div className='header-back-btn'>
					<h2>{translations.administration}</h2>
				</div>
				<div className='header-right-btn'>
					<Button buttonSize={"btn--medium"} onClick={onClickAddUser}>
						{translations.addNewUsers}
					</Button>
				</div>
			</section>
			<section className='search-wrapper'>
				<SearchBar
					val={currentSearchText}
					handleChange={onSearchChanged}
					customClasses={["search-bar-full-width"]}
					placeholder={translations.searchUserTable}
					showSearchIcon={true}
				/>
				<Button isDarkButton={true} onClick={() => onClickFilter()}>
					{translations["lang-filter"]}
				</Button>
			</section>
			<FiltersRow
				selectedTabId={selectedTabId}
				isUserView={isUserView}
				userFilters={userFilters}
				educationByUserFilters={educationByUserFilters}
				educationByCourseFilters={educationByCourseFilters}
				onToggleFilter={onToggleFilter}
			/>
			<section className='table-container-general'>
				<div className='user-tab-wrapper'>
					<TabPanel
						tabs={initialTabs}
						onClickTab={onClickTab}
						selectedTabId={selectedTabId}
						customClass='tab-solid-panel-tms'
					/>
					{selectedTabId === 1 && (
						<Button
							isLightButton={true}
							buttonSize={"btn--medium"}
							onClick={() => setIsUserView(!isUserView)}
						>
							{isUserView ? translations.viewByCourse : translations.viewByUser}
						</Button>
					)}
				</div>
				{selectedTabId === 0 && <Table columns={userColumns} rows={userTableData} />}
				{selectedTabId === 1 && (
					<>
						{isUserView ? (
							<Table columns={educationByUserColumns} rows={educationByUserTableData} />
						) : (
							<Table columns={educationByCourseColumns} rows={educationByCourseTableData} />
						)}
					</>
				)}
			</section>
		</ComponentWrapper>
	)
}

export default UsersAdmin